/*
constructor and its arguments (where they come from, what purpose they serve, etc.)
*/
class AddToCartForm {
  /**
   * AddToCartForm constructor accepts an object of data passed from product.add-to-cart-scripts.liquid.
   * The goal is for product.add-to-cart-scripts to 'prepare' any and all liquid data –
   * product data (incl. metafields), customer data (likewise), a string representing the
   * 'template' within which it's called ('pdp', 'plp') – and for this class to perform
   * front-end operations using that data, including DOM manipulation and AJAX-ified
   * add-to-cart behaviour.
   *
   * @param {object} anonymous various datapoints to do with the product, customer and template.
   */
  constructor({
    template, // 'plp' or 'pdp' depending on where it's called (product.card.form.liquid or legacy.product.js.liquid)
    uniqueId, // a unique identifier, generated by the 'utility.random-number' snippet (best as Liquid allows), to handle the same productId's card appearing multiple times per page
    productId, // product.id Liquid property
    customer, // JS object containing id (number); metafields (object), tags (array) – all created from Liquid data;
    getProductData, // Function to return a promise containing (if resolved) an object of product data from Liquid: id (number), json (object; {{product|json}}), metafields (object), variants (object), discount, 'vars' (some starter values used in calculations)
    liquidVars, // An object of 'free-floating' Liquid variables used for calculations, i.e. Liquid variables assigned higher up the file hierarchy and required for calculations below.
    orderLimit, // Limit, as a number (integer or float) and in grams or dollars, of how much a customer can order.
  }) {
    // Cache needed/reused variables and elements as class properties:
    this.uniqueId = uniqueId;
    this.productId = productId;
    this.template = template;
    this.data = null;
    this.customerID = customer.id;
    this.customerMetafields = customer.metafields;
    this.customerTags = customer.tags;
    this.liquidVars = liquidVars;
    this.orderLimit = orderLimit;
    this.getProductData = getProductData;
    this.variantSelected = false;

    this.productForm = liquidVars && liquidVars.productForm ? liquidVars.productForm : '';
    this.maxOrderInsurance = liquidVars && liquidVars.maxOrderInsurance ? liquidVars.maxOrderInsurance : '';
    this.purchaseLimitThreshold = liquidVars && liquidVars.purchaseLimitThreshold ? liquidVars.purchaseLimitThreshold : '';
    this.inCart = liquidVars && liquidVars.inCart ? liquidVars.inCart : '';
    this.totalBeverages = liquidVars && liquidVars.totalBeverages ? liquidVars.totalBeverages : '';
    this.customerCompassionate = liquidVars && liquidVars.customerCompassionate ? liquidVars.customerCompassionate : '';
    this.isAltMadCustomer = this.customerMetafields && this.customerMetafields.is_alt_mad_customer ? this.customerMetafields.is_alt_mad_customer : '';


    // Assign the appropriate form to 'this.$form' based on the template and presence of target form.
    const $plpForm = $(`form.product-variant-form[data-product-id='${this.productId}'][data-unique-id='${this.uniqueId}']`);
    const $pdpForm = $('#multi-variant-add');
    const $pdpRelatedProductsForm = $('.product-variant-form');

    if ('plp' === this.template && $plpForm.length) {
      this.$form = $plpForm;
    } else if ('pdp' === this.template && $pdpForm.length) {
      this.$form = $pdpForm;
      if ($pdpRelatedProductsForm.length) {
        this.$form.add($pdpRelatedProductsForm);
      }
    }

    this.$productNotAdded = 'plp' === this.template ? this.$form.closest('.card').find('.product-not-added') : null;
    this.$window = $(window);
    this.$variantsWrapper = this.$form.find('.variants');
    this.$price = this.$form.find('.form-price');
    this.$submit = this.$form.find('button[type="submit"]'); // $addToCart previously existed, but was functionally equivalent to this. So, removed for redundancy.
    this.$inputNumbers = this.$form.find('input.input-number');
    this.$quantity = $('#quantity');
    this.$productCart = $('#product-cart');
    this.$variantAdds = $('.variant-add');
    this.$totalOrdered = $('.total_ordered');
    this.$totalOrderedQuantity = $('.total-ordered_quantity');

    // Use class methods to prepare the form, variants, etc.:
    this.getProductData()
      .then((data) => {
        this.data = data;
        this.product = data.json;
        this.productMetafields = data.metafields;
        this.variants = data.variants;
        this.discount = data.discount;

        return this.enableForm();
      })
      .catch((err) => {
        console.error(err)
        this.disableForm(this)
      });

    // Bind (delegated) events to class methods:
    this.$form.on('submit', (e) => this.submitFormViaAjax(e, this));
  }

  /**
   * Passes a variant object to methods for handling
   * based on template and product type conditions.
   *
   * This began as a renamed/factored version of the legacy function 'buildQuantitySelector'.
   * Renamed because it doesn't really 'build' any 'quantity selector.'
   * It more passed a variant along to other methods for handling.
   *
   * @param {object} variantObj Shopify variant data passed as an object.
   *
   */
  triageVariant(variantObj = null) {

    if ('plp' === this.template) {
      this.parseVariants(variantObj);
    } else if ('pdp' === this.template) {
      if (this.isMedicalMarijuana()) {
        this.parseVariants(variantObj);
      } else {
        this.$quantity.empty();
        this.parseVariants(variantObj);
        this.$productCart.show();
      }
    }
  }

  /**
   * Returns the '.class-name' for the PDP notification
   * based on customer prescription.cycles metafield
   * and the maxOrderInsurance Liquid var.
   *
   * @return {string} Class name with period, e.g. '.warning-notification'.
   *
   */
  getPDPNotificationClass() {
    let notificationClass = '.warning-notification'

    if (this.customerMetafields.prescription_cycles) {
      if (this.maxOrderInsurance > 0) {
        notificationClass = '.warning-notification-insurance'
      } else {
        notificationClass = '.warning-notification-prescription'
      }
    }
    return notificationClass;
  }

  /**
   * Returns a boolean of whether or not a PLP product can be purchased,
   * based on multiple conditions and calculations.
   *
   * @return {boolean} 'true' if product can be purchased.
   *
   */
  canPLPProductBePurchased() {
    let {
      totalQuantity
    } = this.data.vars;

    const $variants = this.$form.find('.variants');

    let totalGramAmount = 0;
    let productCanBePurchased = true;

    $variants.find('.variant').find('input:selected').each((i, el) => {
      const $el = $(el);

      let calcLimit = 0;

      let equivalentWeight = $el.data('equivalent_weight');
      let bottle = $el.data('size_bottle');
      let equivalencyUnits = $el.data('equivalency_units');
      let quantity = 1;
      let equivalency = parseFloat(equivalentWeight) * parseInt(equivalencyUnits);
      let gramAmount = equivalency * parseInt(quantity);
      let totalBottleSize = 0;

      totalGramAmount = totalGramAmount + gramAmount;
      calcLimit = calcLimit + gramAmount;

      if ((parseInt(calcLimit) + parseFloat(equivalentWeight)) > this.orderLimit) {
        productCanBePurchased = false;
      }

      if ((this.orderLimit - calcLimit) < equivalency) {
        productCanBePurchased = false;
      }

      if (this.orderLimit - calcLimit === 0) {
        productCanBePurchased = false;
      }
      if ('beverages' === this.productMetafields.form) {
        this.totalBeverages = parseInt(this.totalBeverages) + parseInt(quantity);
        if (this.totalBeverages > this.purchaseLimitThreshold) {
          productCanBePurchased = false;
        }
      }

      totalQuantity = totalQuantity + parseInt(quantity);
      totalBottleSize = totalBottleSize + (bottle * parseInt(quantity))

      if (calcLimit > orderLimit + 0.1) {
        productCanBePurchased = false;
      }

      if (calcLimit >= orderLimit) {
        productCanBePurchased = false;
      }
    })
    return productCanBePurchased
  }

  /**
   * Adapted from 'calculateOrderLimit,' the name of which was rather inaccurate,
   * as this method performs a sequence of method calls and/or DOM manipulations
   * based on the product.type.
   *
   */
  processPDPVariant(variantID = null) {
    let orderLimit = this.orderLimit;

    let notificationClass = this.getPDPNotificationClass();

    // Legacy code contained an $.each() loop of the 'this.$variantAdds' that:
    // a) updated variable assignments, b) updated UI. These 'jobs' have been separated.
    // The following method does 'a'...
    const totals = this.getTotalsFromPDPForm();
    // ...And this method does 'b'.
    this.updatePDPVariantAddsUI(variantID, totals);

    // Destructuring for methods that only require totalGramAmount
    const {
      totalGramAmount
    } = totals;

    // A large part of the 'calculateOrderLimit' code
    // has been sorted into 'themed' functions.
    this.updateNotificationClass(notificationClass, totalGramAmount);
    this.updateCartUI(totals);
    this.updatePDPItemsText(totalGramAmount);
  }

  /**
   * Loops through the 'variants add's to get a total, in grams,
   * of items the user intends to 'add to cart'.
   *
   * @returns {number} of total grams queued up in the add-to-cart form.
   *
   */
  getTotalsFromPDPForm() {
    let totalGramAmount = 0;
    let totalQuantity = 0;
    let totalBeverages = this.totalBeverages;

    // calculate current limit based on all items in cart
    this.$variantAdds.each((i, el) => {
      const $el = $(el);
      const $inputNumber = $el.find('.input-number');

      let equivalentWeight = $el.data('equivalent_weight');
      let equivalencyUnits = $el.data('equivalency_units');
      let quantity = $inputNumber.val();
      let equivalency = parseFloat(equivalentWeight) * parseInt(equivalencyUnits)
      let gramAmount = equivalency * parseInt(quantity);
      let quantityOverage = (totalGramAmount - this.orderLimit) / equivalency;

      totalGramAmount = totalGramAmount + gramAmount;

      if (quantity > 0 && quantityOverage > 0) {
        totalQuantity = totalQuantity - quantityOverage;

        if (parseInt($inputNumber.val()) === 0) {
          quantity = 0;
        }
      }

      totalQuantity = totalQuantity + parseInt(quantity);

      if (this.productMetafields.form === 'beverages') {
        totalBeverages = parseInt(totalBeverages) + parseInt(quantity);
      }

    }) // end variant_add loop
    return {
      totalGramAmount,
      totalQuantity,
      totalBeverages
    };
  }

  /**
   * Chunks the $.each loop of '.variant-add's from the legacy PDP code.
   * Performs a mixture of functions: Updates/assigns variables based on calculations,
   * updates UI based on calculations, (now) returns the updated variables as an object
   * for use in their 'parent' method.
   *
   * @param {number} variantID
   * @param {object} anonymous expects the properties totalGramAmount and totalBeverages, with which to determine which UI modifications are needed.
   *
   */
  updatePDPVariantAddsUI(variantID = null, {
    totalGramAmount = 0,
    totalBeverages = 0
  } = {}) {
    // calculate current limit based on all items in cart
    this.$variantAdds.each((i, el) => {
      const $el = $(el);
      const $inputNumber = $el.find('.input-number');
      const $plusButton = $el.find('button.plus');

      const toggleInputNumberAndPlusButton = (status = 'enable') => {
        if (status === 'disable') {
          $plusButton.prop('disabled', true);
          $inputNumber.prop('readonly', true);
        } else if (status === 'enable') {
          $plusButton.prop('disabled', false);
          $inputNumber.prop('readonly', false);
        }
      }

      let roomInCart = true;
      let equivalentWeight = $el.data('equivalent_weight');
      let equivalencyUnits = $el.data('equivalency_units');
      let quantity = $inputNumber.val();
      let equivalency = parseFloat(equivalentWeight) * parseInt(equivalencyUnits)

      let quantityOverage = (totalGramAmount - this.orderLimit) / equivalency;

      if (quantity > 0 && quantityOverage > 0) {
        if (parseInt($inputNumber.val()) === 0) {
          quantity = 0;
        }

        $(`[data-type='minus'][data-field='${variantID}']`).prop('disabled', false)
      }

      if (parseInt(totalGramAmount) + parseFloat(equivalentWeight) > this.orderLimit) {
        roomInCart = false;
      }

      if (this.orderLimit - totalGramAmount === 0) {
        roomInCart = false;
      }

      if (this.productMetafields.form === 'beverages') {
        if (totalBeverages > this.purchaseLimitThreshold) {
          roomInCart = false;
          $('.beverage-overage-alert').removeClass('d-none');
        }
      }

      if (roomInCart === false) {
        toggleInputNumberAndPlusButton('disable');
        this.updateSubmit('disable');
        $inputNumber.trigger('blur');
      } else {
        toggleInputNumberAndPlusButton('enable');
        this.updateSubmit('enable');
      }
    }) // end variant_add loop
  }

  /**
   * Updates the text of PDP items, including:
   * • The '.total-ordered_quantity' span
   * • The '.total-ordered_grams' span
   *
   * @param {number} totalGramAmount that the user is attempting to order.
   *
   */
  updatePDPItemsText(totalGramAmount = 0) {
    const $totalOrderedGrams = $('.total-ordered_grams')
    let totalOrderedQuantityText = ` / ${parseFloat(totalGramAmount).toFixed(2)}g`

    if (this.productMetafields.display_weight === "true") {
      totalOrderedQuantityText = `${parseFloat(totalGramAmount).toFixed(2)}g`
    }

    let totalOrderedGrams = parseFloat(totalGramAmount).toFixed(2)
    let unitOfMeasure = this.getUnitOfMeasure();

    if (this.productMetafields.display_weight === "false") {
      totalOrderedQuantityText = `${parseFloat(totalGramAmount).toFixed(2)}g`
    }

    this.$totalOrderedQuantity.text(totalOrderedQuantityText)
  }

  /**
   * Updates several pieces of UI pertaining to the cart:
   * • Enables/disables and/or shows/hides the #add-to-cart element;
   * • Shows/hides the .total_ordered element.
   *
   * @param {object} anonymous expects current order totals: totalGramAmount and totalQuantity.
   *
   */
  updateCartUI({
    totalGramAmount = 0,
    totalQuantity = 0
  } = {}) {
    let orderLimit = this.orderLimit;

    if (totalQuantity > 0) {
      this.$totalOrdered.removeClass('d-none');
      if (totalGramAmount <= orderLimit || !this.isMedicalMarijuana()) {
        this.updateSubmit('enable');
      } else {
        this.updateSubmit('disable');
      }
    } else {
      this.$totalOrdered.addClass('d-none');
      this.updateSubmit('disable');
    }
  }

  /**
   * Shows/hides the 'notificationClass' element (which, itself, is passed as an argument).
   *
   *  @param {string} notificationClass to locate and manipulate with jQuery.
   *  @param {number} totalGramAmount that the user is attempting to order.
   *
   */
  updateNotificationClass(notificationClass = null, totalGramAmount = 0) {
    if (notificationClass) {
      const $notificationClass = $(notificationClass);
      $notificationClass.addClass('d-none')

      if (totalGramAmount > this.orderLimit + 0.1) {
        $notificationClass.removeClass('d-none');
      } else {
        $notificationClass.addClass('d-none');
      }

      if (totalGramAmount >= this.orderLimit) {
        $notificationClass.removeClass('d-none')
      }
    }
  }

  /**
   * Invokes specific 'processing' method based on template.
   *
   * (Currently, a quite superfluous method. Exists in aniticipation of PLP possibly needing
   * an 'else' condition which could/would call its own method.)
   *
   * @param {number} variantID ID of the variant to run calculations for.
   *
   */
  processVariant(variantID) {
    // calculate current limit based on all items in cart
    if ('pdp' === this.template) {
      this.processPDPVariant(variantID);
    }
  }

  /**
   * Disables product inputs inside the $variantsWrapper.
   * Called within disableForm method.
   *
   */
  disableProductInput() {
    this.$variantsWrapper.find('input').prop('disabled', true);

    if (this.$productNotAdded.length) {
      this.updateSubmit('disable');
    }
  }

  /**
   * Enables or disables the submit (Add to Cart) button
   * based on an 'enable'/'disable' parameter.
   *
   * @param {string} status Expects 'enable' (default) or 'disable'.
   *
   */
  updateSubmit(status = 'enable') {
    if ('disable' === status) {
      this.$submit.prop('disabled', true);
    } else if ('enable' === status) {
      this.$submit.prop('disabled', false);
    }
  }

  /**
   * Returns true if product.type is Medical Marijuana.
   *
   * @returns {boolean} true if product type is Medical Marijuana, false if not.
   */
  isMedicalMarijuana() {
    return this.product.type === 'Medical Marijuana' ? true : false;
  }

  /**
   * Returns unit of measure as a string from product metafields.
   *
   * @return {string} of the unit of measure, e.g. 'g'.
   *
   */
  getUnitOfMeasure() {
    let unitOfMeasure = '';

    if (this.productMetafields.unit_of_measure_display) {
      unitOfMeasure = this.productMetafields.unit_of_measure_display
    } else if (this.productMetafields.unit_of_measure) {
      unitOfMeasure = this.productMetafields.unit_of_measure
    }

    return unitOfMeasure;
  }

  /**
   * Returns an object of price-related variables.
   *
   * @param {object} variant jQuery object of the filter group form.
   *
   * @return {number} the variant's price (discounted if applicable).
   *
   */
  getPrice(variant) {
    const {
      canDiscount,
      discount
    } = this.discount;

    let price = variant.price

    if (canDiscount === 1 && this.customerCompassionate !== 0) {
      price = variant.price * discount;
    }

    return price;
  }

  /**
   * Sets several data-attributes of a variant element.
   *
   * @param {jQuery} $filterColumn jQuery object of the filter group form.
   * @param {object} variantObj Shopify variant data passed as an object.
   *
   */
  updateVariantAttrs($variantEl = null, variantObj = null) {

    if (!$variantEl || !variantObj) return;
    const unitOfMeasure = this.getUnitOfMeasure();
    const price = this.getPrice(variantObj);

    if ('pdp' === this.template) {
      const $variantByID = $(`#${variantObj.id}`)
      if (!this.productMetafields.display_weight) {
        $variantEl.find('.measure_multiple_amount').text(variantObj.metafields.equivalent_weight)
      }
    }

    $variantEl.data('price', '$' + (parseFloat(price) * 0.01).toFixed(2));
    $variantEl.data('unit_of_measure', unitOfMeasure);
    $variantEl.data('equivalent_weight', variantObj.metafields.equivalent_weight);
    if (this.isAltMadCustomer) $variantEl.data('equivalent_weight', variantObj.metafields.cnesst_dfe);
    $variantEl.data('equivalency_units', variantObj.metafields.equivalency_units);
    $variantEl.data('inventory_quantity', variantObj.inventory_quantity);
    $variantEl.data('health_canada_dfe', variantObj.metafields.health_canada_dfe);
  }

  /**
   * Updates multiple UI elements based on variant data.
   *
   * Some code from the legacy function 'populateSelectors' has been relocated here
   * where it handled manipulations to DOM/UI elements based on variant data.
   *
   * @param {jQuery} $filterColumn jQuery object of the filter group form.
   * @param {object} variantObj Shopify variant data passed as an object.
   *
   */
  updateVariantUI($variantEl = null, variantObj = null) {
    if (!$variantEl || !variantObj) return;

    let price = this.getPrice(variantObj);

    if ('pdp' === this.template) {
      const $vetDiscountMessage = $('#vet-discount-message');
      const $propertiesVariantEquivalency = $('#properties_variant_equivalency');
      const {
        canDiscount,
        discount
      } = this.discount;
      const $variant = $(`#variant-${variantObj.id}`);

      // clear dropdown
      this.$quantity.empty();
      // add options
      let unitEquivalentWeight = parseFloat(variantObj.metafields.equivalent_weight) / parseInt(variantObj.metafields.equivalency_units);

      $propertiesVariantEquivalency.val(unitEquivalentWeight);

      this.$quantity.on('change', () => {
        let eligibleGrams = this.$quantity.val() * unitEquivalentWeight;
      });

      if (variantObj.price > 850) {
        $vetDiscountMessage.show();
        this.$quantity.trigger('change');
      } else {
        $vetDiscountMessage.hide();
      }

      this.$productCart.show();

      if (canDiscount === 1 && this.customerTags.includes('compassion')) {
        price = variantObj.price * discount;
      }

      $variant.find('.product-price').text('$' + (parseFloat(price) * 0.01).toFixed(2))
      if (this.productMetafields.display_weight === "false") {
        $variant.find('.measure_multiple_amount').text(variantObj.metafields.measure_multiple_amount)
      } else {
        $variant.find('.measure_multiple_amount').text(variantObj.metafields.equivalent_weight)
      }

    } else if ('plp' === this.template) {
      const unitOfMeasure = this.getUnitOfMeasure();

      const $radio = this.$form.find(`input#variant-${variantObj.id}-${this.uniqueId}`);
      const $label = this.$form.find(`label[for='variant-${variantObj.id}-${this.uniqueId}']`);

      if ($radio.length && $label.length) {
        if (this.isMedicalMarijuana()) {
          if (this.productMetafields.display_weight === 'true') {
            $label.text(variantObj.title)
          } else {
            if (this.productMetafields.display_weight === 'false') {
              $label.text(`${variantObj.metafields.measure_multiple_amount} ${unitOfMeasure}`)
            } else {
              $label.text(`${variantObj.metafields.equivalent_weight} ${unitOfMeasure}`)
            }
          }
        } else {
          $label.text(variantObj.title)
        }

        // Disable variant by default:
        $radio.prop('disabled', true);

        // For each available variant, enable it along with the
        // submit button, and check the first available variant:
        if (variantObj.available) {

          $radio.prop('disabled', false);

          this.updateSubmit('enable');

          if (this.variantSelected === false) {

            $radio.prop('checked', true);

            this.variantSelected = true;
          }
        }
      }
    }
  }

  /**
   * Primarily calls a series of methods to update, e.g., attrs and UI elements of a variant.
   *
   * @param {object} variantObj Shopify variant data passed as an object.
   *
   */
  parseVariants(variantObj = null) {
    if (!variantObj) return;

    let $variantEl = null;

    if ('plp' === this.template) {
      $variantEl = this.$form.find(`.variant[data-variant-id='${variantObj.id}']`)
    } else {
      $variantEl = $(`tr[id='variant-${variantObj.id}']`);
    }

    // Edited here because used in both the 'update' functions below.
    if (!variantObj.metafields.equivalent_weight) {
      variantObj.metafields.equivalent_weight = '';
    }

    this.updateVariantAttrs($variantEl, variantObj);
    this.updateVariantUI($variantEl, variantObj);
  }

  /**
   * Handles changes to the $variantsWrapper on PLP template.
   * Changes the price text and enables the submit button.
   *
   */
  handleVariantsChange() {
    this.$variantsWrapper.on('change', '.variant input', (e) => {

      const $input = $(e.target);

      if ($input.is(':checked')) {

        const $activeVariant = $input.closest('.variant');

        this.$price.text(
          $activeVariant.data('price')
        );

        this.$submit.prop('disabled', false);
      }
    });
  }

  /**
   * Handles events on various form items.
   * c. July 2024: Only on PDP template (for now?),
   * but uses a condition in the event it’s expanded later.
   *
   * Called within enableForm().
   *
   * @param {object} variant object, the ID of which is used in several conditions and template literals.
   *
   */
  handleEvents(variant = null) {
    if ('pdp' === this.template) {
      if (variant && variant.id) {
        const $variantButton = $(`tr#variant-${String(variant.id)}`).find('button.btn-number');

        // Utility function to handle UI changes of 'minus buttons'
        const updateMinusButtons = () => {
          const $minusBtn = $(`[data-field='variant-${variant.id}'][data-type='minus']`)
          const value = parseInt($(`[data-variant-id='variant-${variant.id}']`).val())

          if (value <= 0) {
            $minusBtn.prop('disabled', true)
          } else {
            $minusBtn.prop('disabled', false)
          }
        }

        // Handle $variantButton click
        $variantButton.on('click', (e) => {

          const $target = $(e.currentTarget);
          const type = $target.data('type')
          const variantID = $target.data('field')
          const $plusButton = $(`[data-field='${variantID}'][data-type='plus']`)

          let value = parseInt($(`[data-variant-id='${variantID}']`).val())
          let inventoryThreshold = 0;

          if (window.keyupTimer) {
            clearTimeout(window.keyupTimer);
          }

          if (isNaN(value)) {
            value = 0
          }

          if (type === 'plus') {

            if (this.productMetafields.inventory_threshold) {
              inventoryThreshold = this.productMetafields.inventory_threshold;
            }
            let threshold = parseInt(inventoryThreshold)

            if (!this.isMedicalMarijuana()) {
              threshold = 0
            }

            let inventoryQuantity = parseInt($(`#${variantID}`).data('inventory_quantity'))

            if ((inventoryQuantity > threshold) && (value < inventoryQuantity - threshold)) {
              value = value + 1
            } else {
              $plusButton.prop('disabled', true)
            }
          } else {
            if (value !== 0) {
              value = value - 1
              $plusButton.prop('disabled', false)
            }
          }

          $(`[data-variant-id='${variantID}']`).val(parseInt(value))
          this.processVariant(variantID)

          updateMinusButtons();
        })

        // Handle variant-specific .input-number focus
        const $inputNumberWithVariantID = $(`input.input-number#variant-${variant.id}`)

        $inputNumberWithVariantID.on('focus', (e) => {
          if (0 === Number(e.currentTarget.value) || (!e.currentTarget.value || isNaN(e.currentTarget.value))) {
            e.currentTarget.value = '';
          }
        })

        // Handle variant-specific .input-number blur
        $inputNumberWithVariantID.on('blur', (e) => {

          const variantID = $(e.currentTarget).data('variant-id')
          if (!e.currentTarget.value || parseInt(e.currentTarget.value) < 1) {
            e.currentTarget.value = 0;
          }

          // 'originalEvent' condition to check if a click is human-made or programmatic,
          // in order to prevent a recursive loop with 'calculateOrderLimit()', which
          // _triggers_ a blur event itself, firing this, which would fire that...
          if (Object.prototype.hasOwnProperty.call(e, 'originalEvent')) {
            if (window.keyupTimer) {
              clearTimeout(window.keyupTimer);
            }

            if (e.currentTarget.value <= 0) {
              window.keyupTimer = setTimeout(() => {
                this.processVariant(variantID)
              }, 500);
            }
          }

          updateMinusButtons();

        })

        // Handle all keyup for all '.input-number's
        const $inputNumber = $('input.input-number');

        $inputNumber.on('keyup', (e) => {
          const key = e.key.toLowerCase();
          const isLetter = (key >= 'a' && key <= 'z');
          if (isLetter) {
            e.preventDefault();
          }

          if (window.keyupTimer) {
            clearTimeout(window.keyupTimer);
          }

          let value = e.currentTarget.value;

          if (value > 0) {
            let variantID = $(e.currentTarget).data('variantId')
            let inventoryThreshold;

            if (this.productMetafields.inventory_threshold) {
              inventoryThreshold = this.productMetafields.inventory_threshold;
            }

            let threshold = parseInt(inventoryThreshold)

            if (this.isMedicalMarijuana()) {
              threshold = 0
            }

            let inventoryQuantity = parseInt($(`#${variantID}`).data('inventory_quantity'))

            if (value > inventoryQuantity - threshold) {
              value = inventoryQuantity - threshold
              e.currentTarget.value = value
            }

            const $minusBtn = $(`[data-field='${variantID}'][data-type='minus']`);

            if (value <= 0) {
              $minusBtn.prop('disabled', true)
            } else {
              $minusBtn.prop('disabled', false)
            }

            if (this.productMetafields.form === 'beverages') {
              if (this.totalBeverages + value > this.purchaseLimitThreshold) {
                value = this.purchaseLimitThreshold - this.totalBeverages;
                e.currentTarget.value = value;
                $('.beverage-overage-alert').removeClass('d-none');
                this.$submit.prop('disabled', true);
              }
            }

            window.keyupTimer = setTimeout(() => {
              this.processVariant(variantID)
            }, 500);
          }

          updateMinusButtons();
        })

        // Prevent default on 'Enter' keydown
        this.$window.on('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            return false;
          }
          return null;
        });
      }
    }
  }

  /**
   * Returns a jQuery object consisting of .input-number
   * elements with a value (quantity) greater than 0.
   *
   * @returns {jQuery}
   */
  getPDPInputsWithQuantity() {
    return $('.input-number').filter((index, element) => {
      return parseInt(element.value) > 0;
    });
  }

  /**
   * Prepares and returns an object of values used in the Shopify ajax add to cart function.
   *
   * @param {object} el HTML element used to find variant ID on PDP template.
   *
   * @returns {object} of data (id, properties, quantity) used by the Shopify ajax add to cart function.
   *
   */
  getAddToCartValues(el = null) {
    let properties = {}
    let values = {};
    let quantity = null;
    let id = null;
    let unitEquivalentWeight = null;
    let eligibleGrams = null;
    let $checkedVariant = null;
    let thisVariantData = null;
    let totalQuantity = 0;
    let numInCart = 0;

    if ('pdp' === this.template) {
      if (el) {
        const $el = $(el);

        values = {}
        quantity = parseInt($el.val());
        id = $el.data('variant-id');
        unitEquivalentWeight = $(`#${id}`).data('equivalent_weight');
        eligibleGrams = quantity * unitEquivalentWeight;
      }
    } else if ('plp' === this.template) {
      const $productVariantForm = $(`.product-variant-form[data-product-id='${this.productId}'][data-unique-id='${this.uniqueId}']`);

      $checkedVariant = this.getCheckedVariant();
      values = {}
      quantity = 1;
      id = $checkedVariant.find('input:checked').val();
      unitEquivalentWeight = $checkedVariant.data('equivalent_weight');

      thisVariantData = this.variants.filter(variant => Number(variant.id) === Number(id))?.[0];
      numInCart = thisVariantData?.num_in_cart ?? numInCart;
      totalQuantity = numInCart > 0 ? quantity + numInCart : quantity;

      // [WS-899] We could probably just re-assign quantity conditionally, but as a hotfix let's start by overhauling as little as possible.
      eligibleGrams = totalQuantity > 1 ?
        totalQuantity * unitEquivalentWeight :
        quantity * unitEquivalentWeight;
    }

    let healthCanadaDFE = 'pdp' === this.template ? $(`#${id}`).data('health_canada_dfe') : $checkedVariant.data('health_canada_dfe');

    properties['_variant_equivalency'] = unitEquivalentWeight;

    properties['_in_cart'] = this.inCart;

    if (this.customerTags.includes('vet')) {
      properties['_order_has_special_pricing'] = true;
    }

    if (this.customerMetafields.prescription && this.customerMetafields.prescription.prescriptionid) {
      properties['_prescriptionid'] = this.customerMetafields.prescription.prescriptionid; // old prescription id, to be removed after switching to new prescription model
    }
    if (this.customerMetafields.prescription && this.customerMetafields.prescription.id) {
      properties['_prescription_id'] = this.customerMetafields.prescription.id; // new prescription id
    }
    if (this.customerMetafields.profile && this.customerMetafields.profile.insurance_id) {
      properties['_insurance_id'] = customerMetafields.profile.insurance_id;
    }
    if (this.liquidVars.prescriptionCycle) {
      properties['_prescription_cycle_id'] = this.liquidVars.prescriptionCycle.id;
    }
    if (this.liquidVars.insuranceCycle) {
      properties['_insurance_cycle_id'] = this.liquidVars.insuranceCycle.id;
    }
    properties['_health_canada_dfe'] = healthCanadaDFE;

    // Shopify AJAX expects the ID to be a number. Unlike HTML IDs.
    // So we have to remove our prefix.
    values = {
      'id': parseInt(id?.replace('variant-', '')),
      'quantity': totalQuantity > 1 ? totalQuantity : quantity,
      'properties': properties,
      'totalQuantity': totalQuantity,
    }

    return values;
  }

  /**
   * Dispatches a custom onAddToCart event with the ID of the added
   * variant, which is consumed by product-variant-analytics.js.
   *
   * @param {string|number} variantId of the added variant.
   */
  sendAddToCartEvent(variantId) {
    document.dispatchEvent(new CustomEvent('onAddToCart', {
      detail: { variantId },
    }));
  }

  /**
   * Prepares and returns an object of values used in the Shopify ajax add to cart function.
   *
   * To be moved/re-built with WS-644.
   *
   * @param {event} e to be handled (preventDefault() called).
   *
   */
  ajaxSubmit(e) {
    e.preventDefault();

    if (this.customerID) {
      Shopify.queue = [];

      let values;

      if ('pdp' === this.template) {
        this.getPDPInputsWithQuantity().each((i, el) => {
          values = this.getAddToCartValues(el);
          Shopify.queue.push(values);
          this.sendAddToCartEvent(values.id);
        });
      } else if ('plp' === this.template) {
        values = this.getAddToCartValues();
        Shopify.queue.push(values);
        this.sendAddToCartEvent(values.id);
      }

      Shopify.syncAjax = () => {
        // If we still have requests in the queue, let's process the next one.
        if (Shopify.queue.length) {
          const request = Shopify.queue.shift();
          const {id, totalQuantity} = request;

          const runAddToCart = () => {
            $.ajax({

              type: 'POST',
              url: '/cart/add.js',
              data: request,
              success: (response) => {
                Shopify.syncAjax();
                window.cartUpdater.update(this.template);
              },
              error: (error) => {
                console.error(error)
                $('.cart-error').removeClass('d-none')
              },
              dataType: 'json'
            });
          }

          // If the item is already in the cart,
          // we'll tack its quantity onto the item being added
          // and scrub it from the cart. Which is done to insure
          // a) the correct discount is being applied, while
          // b) reducing the line items. And then run the 'add to cart'
          // function once the cart has been changed.
          if (totalQuantity > 1) {
            $.ajax({
              url: `/cart/change.js`,
              method: "POST",
              data: {
                id: Number(id),
                quantity: 0 // Set quantity to 0 to remove the item
              },
              success: (response) => {
                Shopify.syncAjax();
                runAddToCart();
              },
              error: (error) => {
                console.error(error)
                $('.cart-error').removeClass('d-none')
              },
              dataType: 'json'
            });
          } else {
            runAddToCart();
          }
        } else {
          if ('pdp' === this.template) {
            $('.ts-vape-alert').removeClass('d-none');
            $('.beverage-alert').removeClass('d-none');
            $('.success-message').removeClass('d-none');
          }
        }
      }
      Shopify.syncAjax()
      e.preventDefault();
    } else {
      console.error('customer.id is required and was not found.')
    }
  }

  /**
   * Returns the .variant that has a currently checked child.
   * Used within PLP template.
   *
   * @returns {jQuery} $checkedVariant jQuery object, based on finding the .variant with a checked input.
   *
   */
  getCheckedVariant() {
    const $checkedVariant = this.$variantsWrapper.find('.variant').filter((i, el) => {
      return $(el).find('input:checked').length;
    });
    return $checkedVariant;
  }

  /**
   * Prepares and returns an object of values used in the Shopify ajax add to cart function.
   *
   */
  enableForm() {
    if ('pdp' === this.template) {
      for (const index in this.variants) {
        const variantObj = this.variants[index];
        this.triageVariant(variantObj);
        this.handleEvents(variantObj);
      }
    }

    if ('plp' === this.template) {
      const canProductBePurchased = this.canPLPProductBePurchased();
      if (canProductBePurchased) {
        this.updateSubmit('enable');

        for (const index in this.variants) {
          let variantObj = this.variants[index];
          this.triageVariant(variantObj);
          this.handleEvents(variantObj);
        }

        const initProductForm = () => {
          const $checkedVariant = this.getCheckedVariant();
          if ($checkedVariant.length) {
            this.$price.text(
              $checkedVariant.eq(0).data('price')
            );
          }

          this.handleVariantsChange();
        }

        initProductForm();
      } else {
        this.disableForm();
      }
    }
  }

  /**
   * Disables the form by calling methods to
   * disable input and submit button elements.
   *
   */
  disableForm() {
    this.disableProductInput();
    this.updateSubmit('disable');
  }

  /**
   * Submits form via AJAX.
   *
   */
  submitFormViaAjax(e) {
    // Add product(s) to the cart, and then...

    e.preventDefault();
    this.ajaxSubmit(e);
  }
}

window.AddToCartForm = AddToCartForm;
